'use client';

import { useMemo } from 'react';
import { cn } from '@/app/utils';
import type { ComparisonTableSectionData } from '../../types';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell } from '../section-shared';

const DEFAULT_COLUMN_COLORS = ['#AC8AFF', '#5AE4D0', '#85ADFF', '#CD7F32'];

const CELL_INLINE_PATTERN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|\{[+-][^}]+\})/g;

function BadgeSuccess({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-[rgba(61,160,112,0.3)] px-3 py-1 font-semibold text-[#3FF99F] text-[11px] leading-[1.5]">
      <svg className="size-3 shrink-0" fill="none" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M5.017 8.517 9.13 4.404 8.312 3.588 5.017 6.883 3.354 5.22l-.817.817 2.48 2.48ZM5.833 11.667a5.81 5.81 0 0 1-2.275-.46 5.872 5.872 0 0 1-1.852-1.247 5.872 5.872 0 0 1-1.247-1.852A5.81 5.81 0 0 1 0 5.833c0-.807.153-1.565.46-2.275a5.872 5.872 0 0 1 1.246-1.852A5.872 5.872 0 0 1 3.558.46 5.81 5.81 0 0 1 5.833 0c.807 0 1.565.153 2.275.46a5.872 5.872 0 0 1 1.852 1.246 5.872 5.872 0 0 1 1.247 1.852c.306.71.46 1.468.46 2.275 0 .807-.154 1.565-.46 2.275a5.872 5.872 0 0 1-1.247 1.852 5.872 5.872 0 0 1-1.852 1.247 5.81 5.81 0 0 1-2.275.46Zm0-1.167a4.65 4.65 0 0 0 3.31-1.39 4.65 4.65 0 0 0 1.357-3.31 4.65 4.65 0 0 0-1.356-3.31A4.65 4.65 0 0 0 5.833 1.166a4.65 4.65 0 0 0-3.31 1.357A4.65 4.65 0 0 0 1.166 5.833a4.65 4.65 0 0 0 1.357 3.31 4.65 4.65 0 0 0 3.31 1.357Z"
          fill="#3FF99F"
        />
      </svg>
      {children}
    </span>
  );
}

function BadgeDanger({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-[rgba(147,0,10,0.2)] px-3 py-1 font-semibold text-[#FFDAD6] text-[11px] leading-[1.5]">
      <svg className="size-3 shrink-0" fill="none" viewBox="0 0 13 12" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M0 11.083 6.417 0l6.416 11.083H0Zm2.013-1.166h8.808L6.417 2.333 2.013 9.917Zm4.404-.584a.57.57 0 0 0 .415-.168.57.57 0 0 0 .168-.415.57.57 0 0 0-.168-.416.57.57 0 0 0-.415-.167.57.57 0 0 0-.416.167.57.57 0 0 0-.167.416c0 .166.056.305.167.415a.57.57 0 0 0 .416.168ZM5.833 7.583H7V4.667H5.833v2.916Z"
          fill="#FFB4AB"
        />
      </svg>
      {children}
    </span>
  );
}

function parseCell(raw: string, highlightColor?: string): React.ReactNode {
  if (!raw) return null;

  let text = raw;
  let highlighted = false;
  if (text.startsWith('*') && !text.startsWith('**')) {
    text = text.slice(1);
    highlighted = true;
  }

  const parts = text.split(CELL_INLINE_PATTERN);
  if (parts.length === 1 && !highlighted) return text;

  const nodes = parts.map((part) => {
    const boldMatch = part.match(/^\*\*(.+)\*\*$/);
    if (boldMatch) return <strong key={part}>{boldMatch[1]}</strong>;

    const linkMatch = part.match(/^\[(.+)\]\((.+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={part}
          className="underline underline-offset-2 hover:text-white/80"
          href={linkMatch[2]}
          rel="noreferrer"
          target="_blank"
        >
          {linkMatch[1]}
        </a>
      );
    }

    const badgeMatch = part.match(/^\{([+-])(.+)\}$/);
    if (badgeMatch) {
      const [, sign, label] = badgeMatch;
      return sign === '+' ? (
        <BadgeSuccess key={part}>{label}</BadgeSuccess>
      ) : (
        <BadgeDanger key={part}>{label}</BadgeDanger>
      );
    }

    return part;
  });

  if (highlighted) {
    return (
      <span className="font-semibold" style={highlightColor ? { color: highlightColor } : undefined}>
        {nodes}
      </span>
    );
  }
  return nodes;
}

interface ColumnDef {
  color: string;
  isFeatured: boolean;
  key: string;
  name: string;
}

export function ComparisonTableSection({ section }: { section: ComparisonTableSectionData }) {
  const rows = section.table?.rows ?? [];
  const headerRow = rows[0];
  const dataRows = rows.slice(1);
  const featuredIdx = section.featuredColumn;
  const userColors = section.columnColors ?? [];

  const columnDefs: ColumnDef[] = useMemo(() => {
    if (!headerRow?.cells?.length) return [];
    return headerRow.cells.slice(1).map((name, i) => ({
      key: `col-${headerRow._key ?? 'h'}-${i}`,
      name: name ?? '',
      color: userColors[i] || DEFAULT_COLUMN_COLORS[i % DEFAULT_COLUMN_COLORS.length],
      isFeatured: featuredIdx === i + 1,
    }));
  }, [headerRow, userColors, featuredIdx]);

  if (!headerRow || dataRows.length === 0) return null;

  return (
    <SectionShell>
      <div className="flex flex-col items-center gap-12">
        <div className="flex w-full flex-col items-center gap-2 text-center">
          <h2 className="font-semibold text-[34px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
          <PortableTextContent className="text-base text-white/65 leading-[1.6]" value={section.description} />
        </div>

        <div className="w-full overflow-x-auto rounded-lg shadow-2xl">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-white/40 border-b">
                <th className="h-20 w-[240px] min-w-[180px] px-6 text-left align-middle font-medium text-sm text-white uppercase tracking-[0.7px] sm:w-[140px] sm:min-w-[140px] sm:px-4 sm:text-xs">
                  {headerRow.cells?.[0] ?? ''}
                </th>
                {columnDefs.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      'relative h-20 min-w-[160px] border-white/[0.16] border-l px-1 text-center align-middle sm:min-w-[120px]',
                      col.isFeatured && 'bg-gradient-to-t from-white/[0.12] to-transparent',
                    )}
                  >
                    <span className="font-semibold text-base text-white">{col.name}</span>
                    <div
                      className="absolute inset-x-0 bottom-0 h-0.5"
                      style={{
                        backgroundColor: col.color,
                        boxShadow: `0 0 15px 0 ${col.color}80`,
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row) => {
                const cells = row.cells ?? [];
                const dimensionLabel = cells[0] ?? '';
                const valueCells = cells.slice(1);

                return (
                  <tr key={row._key} className="border-white/[0.16] border-b">
                    <td className="h-16 px-6 align-middle font-semibold text-sm text-white sm:px-4 sm:text-xs">
                      {parseCell(dimensionLabel)}
                    </td>
                    {valueCells.map((cell, cellIdx) => {
                      const col = columnDefs[cellIdx];
                      return (
                        <td
                          key={`${row._key}-${col?.key ?? cellIdx}`}
                          className={cn(
                            'h-16 border-white/[0.16] border-l px-4 text-center align-middle text-sm text-white',
                            col?.isFeatured && 'bg-white/[0.08]',
                          )}
                        >
                          {parseCell(cell ?? '', col?.color)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </SectionShell>
  );
}
